import { z } from "zod";
import { Injectable, createInterfaceId } from "@gitlab/needle";
import { Logger, withPrefix } from "@gitlab-org/logging";
import { ServerToClientRpcMessageDefinitionSource, declareRequest } from "@gitlab-org/rpc";
import { RpcMessageSender } from "@gitlab-org/rpc-client";
//#region src/doc_context.ts
const IDocContext = z.object({
	/**
	* The text before the cursor.
	*/
	prefix: z.string().describe("The text before the cursor."),
	/**
	* The text after the cursor.
	*/
	suffix: z.string().describe("The text after the cursor."),
	/**
	* This is most likely path to a file relative to the workspace
	*  but if the file doesn't belong to a workspace, this field is identical to document URI
	*
	* Example: If the workspace root is `/home/user/workspace`
	* and the file is `/home/user/workspace/src/file.txt`,
	* then the filename is `src/file.txt`.
	*/
	fileRelativePath: z.string().describe("Path to a file relative to the workspace, or identical to document URI if file does not belong to a workspace."),
	position: z.object({
		line: z.number(),
		character: z.number()
	}).describe("The position in the document."),
	/**
	* The URI of the document.
	*/
	uri: z.string().describe("The URI of the document."),
	/**
	* languageId of the document
	* @readonly
	*/
	languageId: z.string().describe("languageId of the document"),
	/**
	* The workspace folder that the document belongs to.
	*/
	workspaceFolder: z.object({
		uri: z.string(),
		name: z.string()
	}).optional().describe("The workspace folder that the document belongs to.")
});
//#endregion
//#region src/types.ts
const diagnosticSeverity = z.union([
	z.literal(1),
	z.literal(2),
	z.literal(3),
	z.literal(4)
]);
const diagnosticTag = z.union([z.literal(1), z.literal(2)]);
const diagnostic = z.object({
	range: z.object({
		start: z.object({
			line: z.number().min(0),
			character: z.number().min(0)
		}),
		end: z.object({
			line: z.number().min(0),
			character: z.number().min(0)
		})
	}),
	message: z.string(),
	severity: diagnosticSeverity.optional(),
	code: z.union([z.string(), z.number()]).optional(),
	source: z.string().optional(),
	relatedInformation: z.array(z.any()).optional(),
	tags: z.array(diagnosticTag).optional(),
	data: z.any().optional()
});
const GetDiagnosticsParams = z.object({ fileUri: z.string() });
const GetDiagnosticsResult = z.array(diagnostic);
const GetDiagnosticsRequest = declareRequest("$/gitlab/document-quality/get-diagnostics").withParams(GetDiagnosticsParams).withResponse(GetDiagnosticsResult).build();
//#endregion
//#region src/document_quality_service.ts
let _initClass, _initClass2;
function _applyDecs(e, t, n, r, o, i) {
	var a, c, u, s, f, l, p, d = Symbol.metadata || Symbol.for("Symbol.metadata"), m = Object.defineProperty, h = Object.create, y = [h(null), h(null)], v = t.length;
	function g(t, n, r) {
		return function(o, i) {
			n && (i = o, o = e);
			for (var a = 0; a < t.length; a++) i = t[a].apply(o, r ? [i] : []);
			return r ? i : o;
		};
	}
	function b(e, t, n, r) {
		if ("function" != typeof e && (r || void 0 !== e)) throw new TypeError(t + " must " + (n || "be") + " a function" + (r ? "" : " or undefined"));
		return e;
	}
	function applyDec(e, t, n, r, o, i, u, s, f, l, p) {
		function d(e) {
			if (!p(e)) throw new TypeError("Attempted to access private element on non-instance");
		}
		var h = [].concat(t[0]), v = t[3], w = !u, D = 1 === o, S = 3 === o, j = 4 === o, E = 2 === o;
		function I(t, n, r) {
			return function(o, i) {
				return n && (i = o, o = e), r && r(o), P[t].call(o, i);
			};
		}
		if (!w) {
			var P = {}, k = [], F = S ? "get" : j || D ? "set" : "value";
			if (f ? (l || D ? P = {
				get: _setFunctionName(function() {
					return v(this);
				}, r, "get"),
				set: function(e) {
					t[4](this, e);
				}
			} : P[F] = v, l || _setFunctionName(P[F], r, E ? "" : F)) : l || (P = Object.getOwnPropertyDescriptor(e, r)), !l && !f) {
				if ((c = y[+s][r]) && 7 !== (c ^ o)) throw Error("Decorating two elements with the same name (" + P[F].name + ") is not supported yet");
				y[+s][r] = o < 3 ? 1 : o;
			}
		}
		for (var N = e, O = h.length - 1; O >= 0; O -= n ? 2 : 1) {
			var T = b(h[O], "A decorator", "be", !0), z = n ? h[O - 1] : void 0, A = {}, H = {
				kind: [
					"field",
					"accessor",
					"method",
					"getter",
					"setter",
					"class"
				][o],
				name: r,
				metadata: a,
				addInitializer: function(e, t) {
					if (e.v) throw new TypeError("attempted to call addInitializer after decoration was finished");
					b(t, "An initializer", "be", !0), i.push(t);
				}.bind(null, A)
			};
			if (w) c = T.call(z, N, H), A.v = 1, b(c, "class decorators", "return") && (N = c);
			else if (H.static = s, H.private = f, c = H.access = { has: f ? p.bind() : function(e) {
				return r in e;
			} }, j || (c.get = f ? E ? function(e) {
				return d(e), P.value;
			} : I("get", 0, d) : function(e) {
				return e[r];
			}), E || S || (c.set = f ? I("set", 0, d) : function(e, t) {
				e[r] = t;
			}), N = T.call(z, D ? {
				get: P.get,
				set: P.set
			} : P[F], H), A.v = 1, D) {
				if ("object" == typeof N && N) (c = b(N.get, "accessor.get")) && (P.get = c), (c = b(N.set, "accessor.set")) && (P.set = c), (c = b(N.init, "accessor.init")) && k.unshift(c);
				else if (void 0 !== N) throw new TypeError("accessor decorators must return an object with get, set, or init properties or undefined");
			} else b(N, (l ? "field" : "method") + " decorators", "return") && (l ? k.unshift(N) : P[F] = N);
		}
		return o < 2 && u.push(g(k, s, 1), g(i, s, 0)), l || w || (f ? D ? u.splice(-1, 0, I("get", s), I("set", s)) : u.push(E ? P[F] : b.call.bind(P[F])) : m(e, r, P)), N;
	}
	function w(e) {
		return m(e, d, {
			configurable: !0,
			enumerable: !0,
			value: a
		});
	}
	return void 0 !== i && (a = i[d]), a = h(null == a ? null : a), f = [], l = function(e) {
		e && f.push(g(e));
	}, p = function(t, r) {
		for (var i = 0; i < n.length; i++) {
			var a = n[i], c = a[1], l = 7 & c;
			if ((8 & c) == t && !l == r) {
				var p = a[2], d = !!a[3], m = 16 & c;
				applyDec(t ? e : e.prototype, a, m, d ? "#" + p : _toPropertyKey(p), l, l < 2 ? [] : t ? s = s || [] : u = u || [], f, !!t, d, r, t && d ? function(t) {
					return _checkInRHS(t) === e;
				} : o);
			}
		}
	}, p(8, 0), p(0, 0), p(8, 1), p(0, 1), l(u), l(s), c = f, v || w(e), {
		e: c,
		get c() {
			var n = [];
			return v && [w(e = applyDec(e, [t], r, e.name, 5, n)), g(n, 1)];
		}
	};
}
function _toPropertyKey(t) {
	var i = _toPrimitive(t, "string");
	return "symbol" == typeof i ? i : i + "";
}
function _toPrimitive(t, r) {
	if ("object" != typeof t || !t) return t;
	var e = t[Symbol.toPrimitive];
	if (void 0 !== e) {
		var i = e.call(t, r || "default");
		if ("object" != typeof i) return i;
		throw new TypeError("@@toPrimitive must return a primitive value.");
	}
	return ("string" === r ? String : Number)(t);
}
function _setFunctionName(e, t, n) {
	"symbol" == typeof t && (t = (t = t.description) ? "[" + t + "]" : "");
	try {
		Object.defineProperty(e, "name", {
			configurable: !0,
			value: n ? n + " " + t : t
		});
	} catch (e) {}
	return e;
}
function _checkInRHS(e) {
	if (Object(e) !== e) throw TypeError("right-hand side of 'in' should be an object, got " + (null !== e ? typeof e : "null"));
	return e;
}
const DocumentQualityService = createInterfaceId("DocumentQualityService");
let _DefaultDocumentQuali;
(class {
	static {
		[_DefaultDocumentQuali, _initClass] = _applyDecs(this, [Injectable(DocumentQualityService, [Logger, RpcMessageSender])], []).c;
	}
	#logger;
	#sender;
	constructor(logger, sender) {
		this.#logger = withPrefix(logger, "[DocumentQualityService]");
		this.#sender = sender;
	}
	async getDiagnostics(fileUri) {
		try {
			this.#logger.debug(`Getting diagnostics for file: "${fileUri}"`);
			const result = await this.#sender.send(GetDiagnosticsRequest, { fileUri: fileUri.toString() });
			this.#logger.debug(`Received ${result.length} diagnostics for file: "${fileUri}"`);
			return result;
		} catch (error) {
			this.#logger.error(`Failed to get diagnostics for file: "${fileUri}"`, error);
			return [];
		}
	}
	static {
		_initClass();
	}
});
let _DocumentQualityMessa;
(class {
	static {
		[_DocumentQualityMessa, _initClass2] = _applyDecs(this, [Injectable(ServerToClientRpcMessageDefinitionSource, [])], []).c;
	}
	getMessageDefinitions() {
		return [GetDiagnosticsRequest];
	}
	static {
		_initClass2();
	}
});
//#endregion
//#region src/format_diagnostic.ts
function formatDiagnostic(diagnostic) {
	const range = `[${diagnostic.range.start.line}:${diagnostic.range.start.character} - ${diagnostic.range.end.line}:${diagnostic.range.end.character}]`;
	const details = [diagnostic.source ? `source: "${diagnostic.source}"` : void 0, diagnostic.code ? `code: "${diagnostic.code}"` : void 0].filter(Boolean);
	const additionalDetails = details.length ? ` (${details.join(", ")})` : "";
	return `${diagnostic.severity}: ${diagnostic.message} ${range}${additionalDetails}`;
}
//#endregion
//#region src/range_utils.ts
function positionIsAfterOrEqual(pos, other) {
	return pos.line > other.line || pos.line === other.line && pos.character >= other.character;
}
function positionIsBeforeOrEqual(pos, other) {
	return pos.line < other.line || pos.line === other.line && pos.character <= other.character;
}
function rangesOverlap(range1, range2) {
	return positionIsBeforeOrEqual(range1.start, range2.end) && positionIsAfterOrEqual(range1.end, range2.start);
}
function rangeOverlapsAny(range, ranges) {
	return ranges.some((r) => rangesOverlap(range, r));
}
/**
* Formats a Range into a human-readable location string.
* Converts 0-indexed positions to 1-indexed for user display.
*
* @param range - The range to format
* @returns A formatted string describing the range location
*/
function formatRangeLocation(range) {
	const startLine = range.start.line + 1;
	const startCol = range.start.character + 1;
	const endLine = range.end.line + 1;
	const endCol = range.end.character + 1;
	if (startLine === endLine) {
		if (startCol === endCol) return `line ${startLine}:${startCol}`;
		return `line ${startLine}:${startCol}-${endCol}`;
	}
	return `line ${startLine}:${startCol} to line ${endLine}:${endCol}`;
}
//#endregion
//#region src/contributions.ts
const documentContributions = [_DefaultDocumentQuali, _DocumentQualityMessa];
//#endregion
export { _DefaultDocumentQuali as DefaultDocumentQualityService, _DocumentQualityMessa as DocumentQualityMessageDefinitionSource, DocumentQualityService, GetDiagnosticsRequest, IDocContext, documentContributions, formatDiagnostic, formatRangeLocation, rangeOverlapsAny };

//# sourceMappingURL=index.mjs.map